import { Component, OnInit, Output, EventEmitter, Input, signal, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { NgClass, NgIf } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';

// Models
import { ForgotPasswordState } from '../../../../core/models/auth.model';
import { environment } from '../../../../../environments/environment';

// Services
import { AuthApiService } from '../../../../core/services/auth-api.service';



type ErrorType = 'invalid-email' | 'email-not-found' | 'reset-failed' | null;

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  standalone: true,
  imports: [NgClass, NgIf, ReactiveFormsModule]
})
export class ForgotPasswordComponent implements OnInit {
  @Output() hideForgotPwd = new EventEmitter<boolean>();
  @Input() shownEmail = '';


  private readonly fb = inject(FormBuilder);
  private readonly titleService = inject(Title);
  private readonly authApiService = inject(AuthApiService);

  // Signals 
  resetState = signal<ForgotPasswordState>('idle');
  errorType = signal<ErrorType>(null);
  successMessage = signal<string>('');

  // Getters
  get email() { return this.forgotPasswordForm.get('email'); }
  get isLoading(): boolean { return this.resetState() === 'loading'; }
  get isSuccess(): boolean { return this.resetState() === 'success'; }
  get hasError(): boolean { return this.resetState() === 'error'; }
  get showInvalidEmailError(): boolean { return this.errorType() === 'invalid-email'; }
  get showEmailNotFoundError(): boolean { return this.errorType() === 'email-not-found'; }
  get showResetFailedError(): boolean { return this.errorType() === 'reset-failed'; }

  forgotPasswordForm: FormGroup;
  readonly forgotPassPreMessage = 'Please check your email!';

  constructor() {
    this.forgotPasswordForm = this.fb.nonNullable.group({
      email: ['', [
        Validators.required,
        Validators.email,
        Validators.pattern(/^[\w0-9_\-.]+@[\w0-9_\-.]+\.[\w]{2,}$/)
      ]]
    });
  }

  ngOnInit(): void {
    this.titleService.setTitle('Forgot Password');

    if (this.shownEmail) {
      this.forgotPasswordForm.patchValue({ email: this.shownEmail });
    }
  }

  async resetPassword(): Promise<void> {
    this.forgotPasswordForm.markAllAsTouched(); // mark as touched to show validation errors

    if (this.forgotPasswordForm.invalid) {
      this.resetState.set('error');
      this.errorType.set('invalid-email');
      return;
    }

    if (this.isLoading) {
      return;
    }

    try {
      this.resetState.set('loading');
      this.clearErrors();

      const email = this.email?.value;
      const realm = environment.hubx_auth_realm;

      await firstValueFrom(this.authApiService.changePassword(email, realm));

      this.resetState.set('success');
      this.successMessage.set(this.forgotPassPreMessage);

    } catch (error) {
      this.resetState.set('error');
      this.handleError(error);
      console.error('Reset password error:', error);
    }
  }

  clearErrors(): void {
    this.errorType.set(null);
    this.successMessage.set('');
  }

  cancel(): void {
    this.hideForgotPwd.emit(true);
  }

  private handleError(error: any): void {
    if (error?.status === 400 || error?.status === 404 || error?.message?.includes('not found')) {
      this.errorType.set('email-not-found');
    } else {
      this.errorType.set('reset-failed');
    }
  }



}